import React, { memo } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { motion } from "framer-motion";
import styled from "@emotion/styled";
import { Server, Database, Cloud, GitBranch, AlertCircle, Link2 } from "lucide-react";
import { ServiceNode as ServiceNodeType, ServiceType } from "../../types/diagram.types";
import { useDiagramStore } from "../../store/diagramStore";
import { useTheme } from "../../context/ThemeContext";

const NodeContainer = styled(motion.div)<{
  $type: ServiceType;
  $isSelected: boolean;
  $isHighlighted: boolean;
  $isHovered: boolean;
  $isDarkMode: boolean;
}>`
  background: ${(props) => {
    const lightColors: Record<ServiceType, string> = {
      main: "linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)",
      internal: "linear-gradient(135deg, #11cdef 0%, #1171ef 100%)",
      external: "linear-gradient(135deg, #f5365c 0%, #f56036 100%)",
      convoy: "linear-gradient(135deg, #fb6340 0%, #fbb140 100%)",
    };
    const darkColors: Record<ServiceType, string> = {
      main: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
      internal: "linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)",
      external: "linear-gradient(135deg, #ef4444 0%, #f97316 100%)",
      convoy: "linear-gradient(135deg, #f59e0b 0%, #eab308 100%)",
    };
    const colors = props.$isDarkMode ? darkColors : lightColors;
    return props.$type ? colors[props.$type] : colors.internal;
  }};
  border-radius: 12px;
  padding: 16px;
  min-width: 200px;
  max-width: 300px;
  box-shadow: ${(props) => (props.$isSelected ? "0 0 0 3px rgba(94, 114, 228, 0.5), 0 10px 30px rgba(0,0,0,0.15)" : props.$isHovered ? "0 10px 30px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.08)")};
  border: 2px solid ${(props) => (props.$isSelected ? "#5e72e4" : "transparent")};
  opacity: ${(props) => (props.$isHighlighted ? 1 : 0.98)};
  transform: ${(props) => (props.$isHighlighted ? "scale(1.05)" : "scale(1)")};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: white;
`;

const NodeTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const NodeBody = styled.div`
  color: white;
  font-size: 12px;
`;

const EndpointList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0;
  max-height: 150px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const EndpointItem = styled.li<{ $isPaused?: boolean }>`
  padding: 4px 8px;
  margin: 2px 0;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  font-size: 11px;
  word-break: break-all;
  opacity: ${(props) => (props.$isPaused ? 0.5 : 1)};
  text-decoration: ${(props) => (props.$isPaused ? "line-through" : "none")};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LinkedIndicator = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #4caf50;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const ConvoyRouteSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const ConvoyRouteItem = styled.div<{ $isPaused?: boolean }>`
  padding: 6px 8px;
  margin: 4px 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 11px;
  opacity: ${(props) => (props.$isPaused ? 0.5 : 1)};
`;

interface CustomNodeData extends ServiceNodeType {
  isConnectable?: boolean;
}

const getIcon = (type: ServiceType) => {
  const iconProps = { size: 20, color: "white" };
  switch (type) {
    case "main":
      return <Server {...iconProps} />;
    case "internal":
      return <Database {...iconProps} />;
    case "external":
      return <Cloud {...iconProps} />;
    case "convoy":
      return <GitBranch {...iconProps} />;
    default:
      return <Server {...iconProps} />;
  }
};

export const ServiceNodeComponent: React.FC<NodeProps<CustomNodeData>> = memo(({ data, isConnectable, selected }) => {
  const { hoveredNodeId, highlightedPath, setHoveredNode, highlightPath, clearHighlight } = useDiagramStore();
  const { isDarkMode } = useTheme();

  const isHovered = hoveredNodeId === data.id;
  const isHighlighted = highlightedPath.includes(data.id);

  const handleMouseEnter = () => {
    setHoveredNode(data.id);
    highlightPath(data.id);
  };

  const handleMouseLeave = () => {
    setHoveredNode(null);
    clearHighlight();
  };

  return (
    <>
      {/* Target handles on all sides */}
      <Handle
        id="target-left"
        type="target"
        position={Position.Left}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          top: "45%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="target-right"
        type="target"
        position={Position.Right}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          top: "45%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="target-top"
        type="target"
        position={Position.Top}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          left: "45%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="target-bottom"
        type="target"
        position={Position.Bottom}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          left: "45%",
        }}
        isConnectable={isConnectable}
      />

      <NodeContainer $type={data.type} $isSelected={!!selected} $isHighlighted={isHighlighted} $isHovered={isHovered} $isDarkMode={isDarkMode} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}>
        {data.linkedDiagramId && (
          <LinkedIndicator title="Linked to another diagram">
            <Link2 size={14} color="white" />
          </LinkedIndicator>
        )}

        <NodeHeader>
          {getIcon(data.type)}
          <NodeTitle>{data.name}</NodeTitle>
        </NodeHeader>

        <NodeBody>
          {data.description && <div style={{ marginBottom: 8, opacity: 0.9 }}>{data.description}</div>}

          {data.endpoints && data.endpoints.length > 0 && (
            <>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Endpoints:</div>
              <EndpointList>
                {data.endpoints.map((endpoint) => (
                  <EndpointItem key={endpoint.id} $isPaused={endpoint.isPaused}>
                    {endpoint.isPaused && <AlertCircle size={10} />}
                    {endpoint.method && <span style={{ fontWeight: 600 }}>{endpoint.method}</span>}
                    {endpoint.path}
                  </EndpointItem>
                ))}
              </EndpointList>
            </>
          )}

          {data.type === "convoy" && data.convoyRoutes && data.convoyRoutes.length > 0 && (
            <ConvoyRouteSection>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Convoy Routes:</div>
              {data.convoyRoutes.map((route) => (
                <ConvoyRouteItem key={route.id} $isPaused={route.isPaused}>
                  <div style={{ fontWeight: 600 }}>{route.name}</div>
                  {route.description && <div style={{ fontSize: 10, opacity: 0.8 }}>{route.description}</div>}
                  {route.isPaused && (
                    <div style={{ fontSize: 10, color: "#ffcdd2" }}>
                      <AlertCircle size={10} style={{ display: "inline" }} /> Paused
                    </div>
                  )}
                </ConvoyRouteItem>
              ))}
            </ConvoyRouteSection>
          )}
        </NodeBody>
      </NodeContainer>

      {/* Source handles on all sides */}
      <Handle
        id="source-right"
        type="source"
        position={Position.Right}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          top: "55%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="source-left"
        type="source"
        position={Position.Left}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          top: "55%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="source-top"
        type="source"
        position={Position.Top}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          left: "55%",
        }}
        isConnectable={isConnectable}
      />
      <Handle
        id="source-bottom"
        type="source"
        position={Position.Bottom}
        style={{
          background: "#5e72e4",
          width: 12,
          height: 12,
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          left: "55%",
        }}
        isConnectable={isConnectable}
      />
    </>
  );
});
